import { ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	ATTRIBUTES,
	fetchConfig,
	SELECTORS,
	publish,
	PUB_SUB_EVENTS,
} from '@/scripts/core/global'
import {
	CartAddWithSections,
	CartUpdateInstructions,
	CartUpdateWithSections,
	getCart,
	getUpdateInstructions,
	renderRawHTMLToDOM,
	renderSectionsOnce,
	updateProgressBar,
} from '@/scripts/core/cart-functions'
import {
	type OptimisticItemData,
	type AssociatedOptimisticItem,
	createOptimisticItemElement,
	createAssociatedOptimisticElement,
	createOptimisticAdditionElements,
	injectOptimisticItem,
	injectOptimisticAdditions,
	removeAllOptimisticItems,
	updateOptimisticCartCount,
	updateOptimisticFooter,
	updateOptimisticProgressBar,
	updateOptimisticTotals,
	disableCheckoutButton,
	enableCheckoutButton,
	setCartLinePricesLoading,
} from '@/scripts/cart/optimistic-cart'
import {
	type LockToken,
	acquireLock,
	releaseLock,
	isLocked,
	setPendingResponse,
	getPendingResponse,
	setOnAllReleasedCallback,
	forceReleaseAllLocks,
} from '@/scripts/cart/optimistic-lock'
import {
	reconcile,
	evictExclusiveItems,
	getCartState,
	isCartStateInitialized,
} from '@/scripts/cart/cart-state'
import {
	cartDebug,
	summarizeCartItems,
	summarizeDomCartItems,
	summarizeInstructions,
} from '@/scripts/cart/optimistic-debug'

function hasAssociatedMembershipProperties(
	properties: Record<string, string> | undefined
): boolean {
	return Boolean(
		properties?._bundle_membership_item ||
		properties?._alcm_item ||
		properties?._activation_item
	)
}

function isAssociatedMembershipElement(el: Element): boolean {
	return (
		el.classList.contains('cart-free_product') ||
		el.hasAttribute('data-optimistic-associated')
	)
}

const PENDING_REMOVAL_ATTR = 'data-optimistic-pending-removal'

function markItemForRemoval(el: HTMLElement): void {
	el.style.maxHeight = `${el.scrollHeight}px`
	el.setAttribute(PENDING_REMOVAL_ATTR, '')
	requestAnimationFrame(() => el.classList.add('cart-item--removing'))
}

export class CartDrawer extends UcoastEl {
	static htmlSelector = 'cart-drawer'
	static selectors = {
		overlay: '[data-uc-cart-drawer-overlay]',
		innerEmpty: '[data-uc-cart-drawer-inner-empty]',
		inner: '[data-uc-cart-drawer-inner]',
		container: '#CartDrawer',
		cartLink: '#CartIconBubble',
		closeButton: '[data-uc-cart-drawer-close-button]',
		noteSummary: '[data-uc-cart-note-summary]',
		noteDetails: '[data-uc-cart-note-details]',
	}
	activeElement?: HTMLElement
	private _isRunningUpdateInstructions = false
	private _justAddedVariantIds = new Set<number>()
	private static CART_RETRY_DELAY_MS = 450
	private static CART_RETRY_ATTEMPTS = 2
	private hasRenderableSections(
		sections: CartAddWithSections['sections'] | undefined
	): boolean {
		if (!sections) return false

		const requiredSections = [
			'cart-drawer-items',
			'cart-icon-bubble',
			'dynamic-progress-bar',
			'dynamic-cart-footer',
			'cart-update-instructions',
		]

		return requiredSections.every((sectionHandle) => {
			const sectionHTML = sections[sectionHandle]
			return typeof sectionHTML === 'string' && sectionHTML.trim() !== ''
		})
	}

	private async requestCartMutationWithRetry(
		url: string,
		body: unknown,
		context: 'update' | 'add'
	): Promise<Record<string, unknown> | null> {
		for (
			let attempt = 1;
			attempt <= CartDrawer.CART_RETRY_ATTEMPTS;
			attempt++
		) {
			try {
				const response = await fetch(url, {
					...fetchConfig(),
					body: JSON.stringify(body),
				})
				const parsed = JSON.parse(await response.text()) as Record<
					string,
					unknown
				>
				const status =
					typeof parsed.status === 'number'
						? parsed.status
						: response.status

				if (
					status === 409 &&
					attempt < CartDrawer.CART_RETRY_ATTEMPTS
				) {
					cartDebug('cart-drawer:request:retry-on-409', {
						context,
						attempt,
						status,
					})
					await new Promise((resolve) => {
						setTimeout(resolve, CartDrawer.CART_RETRY_DELAY_MS)
					})
					continue
				}

				return parsed
			} catch (error) {
				cartDebug('cart-drawer:request:exception', {
					context,
					attempt,
					error:
						error instanceof Error ? error.message : String(error),
				})
				if (attempt < CartDrawer.CART_RETRY_ATTEMPTS) {
					await new Promise((resolve) => {
						setTimeout(resolve, CartDrawer.CART_RETRY_DELAY_MS)
					})
					continue
				}
			}
		}

		return null
	}

	private async recoverMissingSectionsWithLock(lockToken: LockToken) {
		cartDebug('cart-drawer:recoverMissingSectionsWithLock:start', {
			lockToken,
		})
		const sections = this.getSectionsToRender().map(
			(section) => section.section
		)
		const response = await this.requestCartMutationWithRetry(
			window.routes.cart_update_url,
			{
				updates: {},
				sections,
				sections_url: window.routes.cart_url,
			},
			'update'
		)

		if (!response || typeof response !== 'object') {
			cartDebug('cart-drawer:recoverMissingSectionsWithLock:failed')
			this._clearUpdateState()
			releaseLock(lockToken)
			return
		}

		if ('status' in response) {
			cartDebug(
				'cart-drawer:recoverMissingSectionsWithLock:error-response',
				{
					lockToken,
					status: response.status,
				}
			)
			this._clearUpdateState()
			releaseLock(lockToken)
			return
		}

		this.handleServerResponse(response as CartUpdateWithSections, lockToken)
	}

	constructor() {
		super()

		this.addEventListener(
			'keyup',
			(evt) => evt.code === 'Escape' && this.close()
		)
		this.getOverlay().addEventListener('click', this.close.bind(this))
		this.setHeaderCartIconAccessibility()
		this.maybeAutoOpen()
	}

	getOverlay() {
		return q.rs(CartDrawer.selectors.overlay, this)
	}

	maybeAutoOpen() {
		const url = new URL(window.location.href)
		const urlParams = url.searchParams
		if (urlParams.get('open') === 'cart') {
			this.open()
		}
	}

	setHeaderCartIconAccessibility() {
		const cartLink = q.rs(CartDrawer.selectors.cartLink)
		cartLink.setAttribute('role', 'button')
		cartLink.setAttribute('aria-haspopup', 'dialog')
		cartLink.addEventListener('click', (event) => {
			event.preventDefault()
			this.open(cartLink)
		})
		cartLink.addEventListener('keydown', (event) => {
			if (event.code?.toUpperCase() === 'SPACE') {
				event.preventDefault()
				this.open(cartLink)
			}
		})
	}

	open(triggeredBy?: HTMLElement) {
		if (triggeredBy) this.setActiveElement(triggeredBy)
		const cartDrawerNote = q.os(CartDrawer.selectors.noteSummary, this)
		if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
			this.setSummaryAccessibility(cartDrawerNote)
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add('animate', 'active')
		}, 1)
		// this.closeHeader()
		publish(PUB_SUB_EVENTS.cartOpened)

		// Track Cart Viewed — skip when an optimistic add is in progress.
		// trackCartViewed fires GET /cart.js which competes for the session
		// lock. It will be called from renderContents after the cycle ends.
		if (!this.querySelector('[data-optimistic-item]')) {
			this.trackCartViewed()
		}

		this.addEventListener(
			'transitionend',
			() => {
				const containerToTrapFocusOn = this.hasAttribute(
					ATTRIBUTES.cartEmpty
				)
					? q.os(CartDrawer.selectors.innerEmpty, this)
					: q.os(CartDrawer.selectors.container)
				const focusElement =
					q.os(CartDrawer.selectors.inner, this) ||
					q.os(CartDrawer.selectors.closeButton, this)
				if (containerToTrapFocusOn) {
					window.TsDOM.trapFocus(containerToTrapFocusOn, focusElement)
				}
				void window.Ucoast.mediaManager.playAllInContainer(this)
			},
			{ once: true }
		)

		// Lock Scroll
		const scrollY = window.scrollY;
		document.body.style.top = `-${scrollY}px`
		document.body.classList.add('no-scroll')
	}

	close() {
		this.classList.remove('active')
		// Clean up optimistic items on close (in the render path, section HTML replaces them atomically)
		const itemsEl = q.os('cart-drawer-items', this)
		if (itemsEl) removeAllOptimisticItems(itemsEl)
		window.TsDOM.removeTrapFocus(this.activeElement)

		// Unlock Scroll
		const currentPosition = document.body.style.top
		document.body.classList.remove('no-scroll')
		document.body.style.top = ''
		window.scrollTo(0, parseInt(currentPosition || '0') * -1)
	}

	openWithOptimisticItem(
		data: OptimisticItemData,
		associated?: AssociatedOptimisticItem[]
	) {
		const itemsEl = q.os('cart-drawer-items', this)
		cartDebug('cart-drawer:openWithOptimisticItem:start', {
			main: {
				variantId: data.variantId,
				title: data.title,
				sortGroup: data.sortGroup,
				quantity: data.quantity,
				isExclusive: data.isExclusive,
			},
			associated: (associated ?? []).map((item) => ({
				type: item.type,
				variantId: item.variantId,
				title: item.title,
			})),
			domItems: summarizeDomCartItems(itemsEl ?? null),
		})
		if (!itemsEl) {
			if (!this.classList.contains('active')) this.open()
			return
		}

		// ── Exclusive item rules ────────────────────────────────────
		// Bundles: only 1 allowed in cart, qty always 1
		// Memberships: only 1 allowed in cart, qty always 1
		// Adding the same exclusive → just open drawer (no-op)
		// Adding a different exclusive → evict old, show new
		const hasExclusiveAssociated = associated?.some(
			(a) => a.type === 'bundle_membership' || a.type === 'alcm'
		)
		const isExclusiveAdd = data.isExclusive || hasExclusiveAssociated

		// Build a set of associated variant IDs so we can detect when a
		// predicted membership matches an existing *associated* cart item.
		// Standalone memberships should still be replaced by bundled ones,
		// even when they share a variant ID.
		const associatedVariantIds = new Set(
			(associated ?? [])
				.filter((a) => a.variantId != null)
				.map((a) => a.variantId!)
		)

		if (isExclusiveAdd) {
			// Same-item check: CartState + DOM (covers optimistic items too)
			const sameInState =
				isCartStateInitialized() &&
				getCartState().items.some(
					(item) => item.variantId === data.variantId
				)
			const sameInDom = !!itemsEl.querySelector(
				`[data-quantity-variant-id="${data.variantId}"]`
			)

			if (sameInState || sameInDom) {
				cartDebug(
					'cart-drawer:openWithOptimisticItem:same-exclusive-skip',
					{
						sameInState,
						sameInDom,
						variantId: data.variantId,
					}
				)
				if (!this.classList.contains('active')) this.open()
				return
			}

			// ── Collapse existing optimistic exclusive items ──────────
			// Optimistic items aren't in CartState until finalRender, so
			// scan the DOM directly for previous optimistic bundles,
			// memberships, and associated items.
			const optimisticExclusives = itemsEl.querySelectorAll(
				'[data-optimistic-item][data-optimistic-sort-group="1"], ' +
					'[data-optimistic-item][data-optimistic-sort-group="2"], ' +
					'[data-optimistic-associated]'
			)
			for (const el of optimisticExclusives) {
				if (el instanceof HTMLElement) {
					markItemForRemoval(el)
				}
			}
			cartDebug(
				'cart-drawer:openWithOptimisticItem:marked-optimistic-exclusives',
				{
					count: optimisticExclusives.length,
					domItems: summarizeDomCartItems(itemsEl),
				}
			)

			// ── Collapse server-rendered exclusives via CartState ─────
			if (isCartStateInitialized()) {
				const exclusiveItems = getCartState().items.filter(
					(item) =>
						item.isMembership ||
						item.isBundle ||
						item.isStandaloneTrial
				)
				cartDebug(
					'cart-drawer:openWithOptimisticItem:exclusive-state-before-evict',
					{
						exclusiveItems: summarizeCartItems(exclusiveItems),
					}
				)

				const { state, removedIndices } = evictExclusiveItems()
				cartDebug(
					'cart-drawer:openWithOptimisticItem:exclusive-state-after-evict',
					{
						remainingItems: summarizeCartItems(state.items),
						removedIndices,
					}
				)

				for (const removedIndex of removedIndices) {
					const lineByIndex = itemsEl.querySelector(
						`#CartDrawer-Item-${removedIndex}`
					)
					if (lineByIndex instanceof HTMLElement) {
						markItemForRemoval(lineByIndex)
					}
				}

				if (exclusiveItems.length > 0) {
					for (const item of exclusiveItems) {
						const vid = item.variantId
						// If this membership is the same variant the associated
						// prediction will re-add, keep it in place only when it is
						// already an associated membership — no need
						// for a collapse + re-inject cycle.
						if (
							associatedVariantIds.has(vid) &&
							hasAssociatedMembershipProperties(item.properties)
						)
							continue

						const qtyInput = itemsEl.querySelector(
							`[data-quantity-variant-id="${vid}"]`
						)
						const lineItem = qtyInput?.closest(
							'[data-uc-cart-item]'
						)
						if (lineItem instanceof HTMLElement) {
							markItemForRemoval(lineItem)
						}
					}
					updateOptimisticTotals(state.totals)
				}
			} else {
				// CartState not yet hydrated — collapse server-rendered
				// exclusives from DOM as a fallback. Includes bundles
				// (data-sort-group="1") and memberships (data-sort-group="2").
				const serverExclusives = itemsEl.querySelectorAll(
					'[data-sort-group="1"][data-uc-cart-item]:not([data-optimistic-item]), ' +
						'.cart-free_product[data-uc-cart-item]:not([data-optimistic-item]), ' +
						'[data-sort-group="2"][data-uc-cart-item]:not([data-optimistic-item])'
				)
				for (const el of serverExclusives) {
					if (el instanceof HTMLElement) {
						// Check if this element's variant matches an associated prediction
						const vidAttr = el
							.querySelector<HTMLElement>(
								'[data-quantity-variant-id]'
							)
							?.getAttribute('data-quantity-variant-id')
						if (
							vidAttr &&
							associatedVariantIds.has(Number(vidAttr)) &&
							isAssociatedMembershipElement(el)
						)
							continue

						markItemForRemoval(el)
					}
				}
			}
		}

		// ── Merge or inject main item ───────────────────────────────
		// For regular products: if already in cart, bump quantity.
		// For exclusive items: never merge (handled above — same returns
		// early, different evicts first then falls through to inject).
		let mergedIntoExisting = false
		if (!isExclusiveAdd) {
			const existingQtyInput = itemsEl.querySelector<HTMLInputElement>(
				`[data-quantity-variant-id="${data.variantId}"]`
			)
			if (existingQtyInput) {
				const currentQty = parseInt(existingQtyInput.value || '1', 10)
				const newQty = currentQty + data.quantity
				existingQtyInput.value = String(newQty)
				mergedIntoExisting = true

				const lineItem = existingQtyInput.closest('[data-uc-cart-item]')
				if (lineItem) {
					const priceEl = lineItem.querySelector('.price--end')
					if (priceEl) {
						const unitPrice = isCartStateInitialized()
							? (getCartState().items.find(
									(i) => i.variantId === data.variantId
								)?.price ?? data.price)
							: data.price
						priceEl.textContent = `$${((unitPrice * newQty) / 100).toFixed(2)}`
					}
				}
			}
		}

		if (!mergedIntoExisting) {
			const element = createOptimisticItemElement(data)
			injectOptimisticItem(itemsEl, element, data.sortGroup)
			cartDebug('cart-drawer:openWithOptimisticItem:injected-main', {
				variantId: data.variantId,
				domItems: summarizeDomCartItems(itemsEl),
			})
		}

		// ── Inject associated items (memberships, GWP) ──────────────
		// When evicting exclusives, we already collapsed old memberships —
		// don't skip injection just because the old one is still in DOM
		// (it's animating out). Check for non-removing memberships only.
		if (associated?.length && !mergedIntoExisting) {
			for (const assoc of associated) {
				if (
					assoc.type === 'bundle_membership' ||
					assoc.type === 'alcm'
				) {
					const hasAssociatedMembershipInState =
						isCartStateInitialized() &&
						getCartState().items.some(
							(item) =>
								item.isMembership &&
								hasAssociatedMembershipProperties(
									item.properties
								)
						)

					// If the same variant was kept in place (not evicted),
					// skip injecting a duplicate optimistic element.
					if (assoc.variantId) {
						const keptInPlaceEl = itemsEl
							.querySelector(
								`[data-quantity-variant-id="${assoc.variantId}"]`
							)
							?.closest(
								`[data-uc-cart-item]:not(.cart-item--removing):not([${PENDING_REMOVAL_ATTR}])`
							)
						const keptAssociatedInState =
							isCartStateInitialized() &&
							getCartState().items.some(
								(item) =>
									item.variantId === assoc.variantId &&
									item.isMembership &&
									hasAssociatedMembershipProperties(
										item.properties
									)
							)
						if (
							keptAssociatedInState ||
							(keptInPlaceEl &&
								isAssociatedMembershipElement(keptInPlaceEl))
						)
							continue
					}
					const existing = itemsEl.querySelector(
						`.cart-free_product[data-uc-cart-item]:not(.cart-item--removing):not([${PENDING_REMOVAL_ATTR}]), ` +
							`[data-optimistic-associated]:not(.cart-item--removing):not([${PENDING_REMOVAL_ATTR}])`
					)
					if (hasAssociatedMembershipInState || existing) continue
				}
				if (assoc.type === 'gwp') {
					const existingGwps =
						itemsEl.querySelectorAll('[data-gwp-offer]')
					const alreadyExists = Array.from(existingGwps).some(
						(el) =>
							el
								.querySelector('[data-uc-cart-item-name]')
								?.textContent?.trim() === assoc.title
					)
					if (alreadyExists) continue
				}
				const assocEl = createAssociatedOptimisticElement(assoc)
				const assocSortGroup = parseInt(
					assocEl.getAttribute('data-optimistic-sort-group') || '4',
					10
				)
				injectOptimisticItem(itemsEl, assocEl, assocSortGroup)
				cartDebug(
					'cart-drawer:openWithOptimisticItem:injected-associated',
					{
						type: assoc.type,
						variantId: assoc.variantId,
						title: assoc.title,
						domItems: summarizeDomCartItems(itemsEl),
					}
				)
			}
		}
		// Update count badge, footer totals, and progress bar
		const addedQuantity = data.quantity + (associated?.length ?? 0)

		// Calculate payable total for progress bar (shipping threshold calc)
		const payableTotalDelta = data.price * data.quantity

		updateOptimisticCartCount(addedQuantity)
		updateOptimisticFooter({
			isLoading: true,
		})
		setCartLinePricesLoading()
		updateOptimisticProgressBar(payableTotalDelta)
		if (!this.classList.contains('active')) {
			setTimeout(() => this.open(), 1000)
		}
	}

	/**
	 * Track "Cart Viewed" Segment event when the cart drawer opens.
	 * Deferred slightly to avoid competing for network bandwidth with
	 * the main add-to-cart request flow.
	 */
	private trackCartViewed() {
		// Delay tracking fetch so it doesn't compete with the critical
		// cart add/render requests that are likely in-flight
		setTimeout(() => {
			getCart()
				.then((cart) => {
					if ('status' in cart) return

					const products = cart.items.map((item) => ({
						brand: item.vendor,
						category: item.product_type,
						image_url: item.image,
						name: item.product_title,
						price: item.final_price / 100,
						product_id: item.product_id,
						quantity: item.quantity,
						shopify_variant_id: item.id,
						sku: item.sku,
						variant: item.variant_title,
					}))

					const cartCookie =
						document.cookie
							.split('; ')
							.find((row) => row.startsWith('cart='))
							?.split('=')[1] || ''

					if (window.SegmentUser) {
						void window.SegmentUser.sendSegmentEvent(null, {
							eventName: 'Cart Viewed',
							cart_id: cartCookie,
							category: 'Shopify',
							products,
							sent_from: 'Pvolve Web Script',
						})
					}
				})
				.catch((e) => {
					console.error('[Segment] Error tracking Cart Viewed:', e)
				})
		}, 2000)
	}

	setSummaryAccessibility(cartDrawerNote: HTMLElement) {
		const parentElement = cartDrawerNote.parentElement
		if (!parentElement)
			throw new Error(
				'setSummaryAccessibility failed - No parent element found'
			)
		cartDrawerNote.setAttribute('role', 'button')
		cartDrawerNote.setAttribute('aria-expanded', 'false')
		const nextElementSibling = cartDrawerNote.nextElementSibling

		if (
			nextElementSibling instanceof HTMLElement &&
			nextElementSibling.hasAttribute('id')
		) {
			cartDrawerNote.setAttribute('aria-controls', nextElementSibling.id)
		}

		cartDrawerNote.addEventListener('click', (event: MouseEvent) => {
			const currentTarget = q.rct(event)
			const isExpanded = q
				.rs(CartDrawer.selectors.noteDetails, this)
				.hasAttribute('open')
			currentTarget.setAttribute('aria-expanded', `${isExpanded}`)
		})

		parentElement.addEventListener('keyup', q.onKeyUpEscape)
	}

	// ── Optimistic lock-aware rendering ─────────────────────────────

	/**
	 * Handle a server response by checking update instructions and deferring
	 * rendering until all background mutations complete.
	 * Called by product-form, cart-api, and internally during update cycles.
	 */
	handleServerResponse(
		cart: CartAddWithSections | CartUpdateWithSections,
		lockToken: LockToken
	): void {
		const cartUpdateInstructions = getUpdateInstructions(cart)
		cartDebug('cart-drawer:handleServerResponse:start', {
			lockToken,
			responseItemCount: 'items' in cart ? cart.items.length : undefined,
			instructions: summarizeInstructions(cartUpdateInstructions),
		})

		// When manually removing a GWP/bundle line, skip all additions for this pass
		if (window.__skipGwpAdditions) {
			cartUpdateInstructions.additions = []
			cartUpdateInstructions.update_required =
				Object.keys(cartUpdateInstructions.updates).length > 0
		}
		// When in an update cycle, only suppress additions for items we just added
		else if (
			this._isRunningUpdateInstructions &&
			this._justAddedVariantIds.size > 0
		) {
			cartUpdateInstructions.additions =
				cartUpdateInstructions.additions.filter(
					(a) => !this._justAddedVariantIds.has(a.id)
				)
			cartUpdateInstructions.update_required =
				cartUpdateInstructions.additions.length > 0 ||
				Object.keys(cartUpdateInstructions.updates).length > 0
		}
		// For batched adds: if the response includes items that match pending
		// additions, those additions were already handled — suppress them.
		else if (
			'items' in cart &&
			Array.isArray(cart.items) &&
			cartUpdateInstructions.additions.length > 0
		) {
			const hasMatchingAdditionInCart = (
				addition: CartUpdateInstructions['additions'][number]
			): boolean => {
				const additionProperties = addition.properties ?? {}
				const additionIsAssociatedMembership =
					hasAssociatedMembershipProperties(additionProperties)
				const additionIsGwp = Boolean(additionProperties._gwp_item)

				return cart.items.some((item) => {
					if (item.id !== addition.id) return false
					const itemProperties = item.properties ?? undefined

					if (additionIsAssociatedMembership) {
						return hasAssociatedMembershipProperties(itemProperties)
					}

					if (additionIsGwp) {
						return Boolean(itemProperties?._gwp_item)
					}

					return true
				})
			}

			cartUpdateInstructions.additions =
				cartUpdateInstructions.additions.filter(
					(a) => !hasMatchingAdditionInCart(a)
				)
			cartUpdateInstructions.update_required =
				cartUpdateInstructions.additions.length > 0 ||
				Object.keys(cartUpdateInstructions.updates).length > 0
		}

		// Store the latest response for the final render
		setPendingResponse(cart)

		if (cartUpdateInstructions.update_required) {
			// Inject optimistic elements for pending additions so they appear
			// instantly while the background cycle adds them server-side.
			// Skip if matching items are already present in the DOM — either
			// from T=0 optimistic prediction or from server-rendered HTML.
			if (cartUpdateInstructions.additions.length > 0) {
				const itemsEl = q.os('cart-drawer-items', this)
				if (itemsEl) {
					// Filter out additions that already exist in the DOM
					const newAdditions =
						cartUpdateInstructions.additions.filter((a) => {
							const props = a.properties ?? {}
							const isMembership =
								!!props._bundle_membership_item ||
								!!props._alcm_item ||
								!!props._activation_item

							if (isMembership) {
								const hasSameAssociatedMembershipInState =
									isCartStateInitialized() &&
									getCartState().items.some(
										(item) =>
											item.variantId === a.id &&
											item.isMembership &&
											hasAssociatedMembershipProperties(
												item.properties
											)
									)
								const hasSameMembershipInDom =
									!!itemsEl
										.querySelector(
											`[data-quantity-variant-id="${a.id}"]`
										)
										?.closest(
											`[data-uc-cart-item]:not(.cart-item--removing):not([${PENDING_REMOVAL_ATTR}])`
										) ||
									!!itemsEl.querySelector(
										`[data-optimistic-associated][data-optimistic-sort-group="2"][data-optimistic-variant-id="${a.id}"]:not([${PENDING_REMOVAL_ATTR}])`
									)

								if (
									hasSameAssociatedMembershipInState ||
									hasSameMembershipInDom
								)
									return false

								const staleOptimisticMemberships =
									itemsEl.querySelectorAll(
										`[data-optimistic-associated][data-optimistic-sort-group="2"]:not([data-optimistic-variant-id="${a.id}"])`
									)
								for (const stale of staleOptimisticMemberships) {
									stale.remove()
								}
							}

							// Check GWP by variant ID
							if (
								props._gwp_item &&
								(itemsEl
									.querySelector(
										`[data-quantity-variant-id="${a.id}"]`
									)
									?.closest(
										`[data-uc-cart-item]:not(.cart-item--removing):not([${PENDING_REMOVAL_ATTR}])`
									) ||
									itemsEl.querySelector(
										`[data-optimistic-associated][data-optimistic-sort-group="3"][data-optimistic-variant-id="${a.id}"]:not([${PENDING_REMOVAL_ATTR}])`
									))
							)
								return false
							return true
						})
					if (newAdditions.length > 0) {
						const optimisticEls =
							createOptimisticAdditionElements(newAdditions)
						injectOptimisticAdditions(itemsEl, optimisticEls)
						cartDebug(
							'cart-drawer:handleServerResponse:injected-additions',
							{
								newAdditions: summarizeCartItems(newAdditions),
								domItems: summarizeDomCartItems(itemsEl),
							}
						)
					}
				}
			}
			cartDebug('cart-drawer:handleServerResponse:run-update-cycle', {
				lockToken,
				instructions: summarizeInstructions(cartUpdateInstructions),
			})
			// Run background cycle — lock stays held
			void this.runUpdateCycleBackground(
				cartUpdateInstructions,
				lockToken
			)
		} else {
			if (!this.hasRenderableSections(cart.sections)) {
				cartDebug(
					'cart-drawer:handleServerResponse:missing-sections-recovery',
					{
						lockToken,
					}
				)
				void this.recoverMissingSectionsWithLock(lockToken)
				return
			}

			// All done — release lock which triggers finalRender
			cartDebug(
				'cart-drawer:handleServerResponse:release-lock-no-updates',
				{
					lockToken,
				}
			)
			this._clearUpdateState()
			releaseLock(lockToken)
		}
	}

	/**
	 * Run GWP/bundle update cycle in background without rendering.
	 * When cycle completes, releases lock → triggers finalRender.
	 */
	private async runUpdateCycleBackground(
		instructions: CartUpdateInstructions,
		lockToken: LockToken
	): Promise<void> {
		cartDebug('cart-drawer:runUpdateCycleBackground:start', {
			lockToken,
			instructions: summarizeInstructions(instructions),
		})
		const sections = this.getSectionsToRender().map(
			(section) => section.section
		)

		// Process updates (removals) BEFORE additions so that conflicting
		// items are removed first. If additions run first, the recursion
		// returns before updates are applied, losing them entirely
		// (e.g. ALCM removal is lost when bundle_membership is added).
		const hasUpdates = Object.keys(instructions.updates).length > 0

		if (hasUpdates) {
			const updateBody = {
				updates: instructions.updates,
				sections,
				sections_url: window.routes.cart_url,
			}
			try {
				cartDebug(
					'cart-drawer:runUpdateCycleBackground:update-request',
					{
						updates: instructions.updates,
					}
				)
				const parsedState = await this.requestCartMutationWithRetry(
					`${window.routes.cart_update_url}`,
					updateBody,
					'update'
				)
				if (!parsedState || 'status' in parsedState) {
					throw new Error('Update cycle request failed')
				}
				cartDebug(
					'cart-drawer:runUpdateCycleBackground:update-response',
					{
						itemCount: parsedState.item_count,
						items: summarizeCartItems(
							(parsedState.items as CartUpdateWithSections['items']) ??
								[]
						),
					}
				)
				// Recurse: check if more updates are needed
				this.handleServerResponse(
					parsedState as CartUpdateWithSections,
					lockToken
				)
				return
			} catch (error) {
				cartDebug(
					'cart-drawer:runUpdateCycleBackground:update-exception',
					{
						error:
							error instanceof Error
								? error.message
								: String(error),
					}
				)
				console.error('Error running update instructions', error)
			}
		}

		if (instructions.additions.length > 0) {
			this._isRunningUpdateInstructions = true
			this._justAddedVariantIds = new Set(
				instructions.additions.map((a) => a.id)
			)

			try {
				const addBody = {
					items: instructions.additions,
					sections,
					sections_url: window.routes.cart_url,
				}

				cartDebug('cart-drawer:runUpdateCycleBackground:add-request', {
					additions: summarizeCartItems(instructions.additions),
				})
				const addResponseParsed =
					await this.requestCartMutationWithRetry(
						`${window.routes.cart_add_url}`,
						addBody,
						'add'
					)
				if (!addResponseParsed) {
					throw new Error('Add cycle request failed')
				}
				cartDebug('cart-drawer:runUpdateCycleBackground:add-response', {
					hasError: 'status' in addResponseParsed,
					itemCount: addResponseParsed.item_count,
					items: summarizeCartItems(
						(addResponseParsed.items as CartAddWithSections['items']) ??
							[]
					),
				})

				if ('status' in addResponseParsed) {
					console.error(
						'[CartDrawer] GWP addition failed:',
						addResponseParsed
					)
				} else {
					// Recurse: check if more updates are needed
					this.handleServerResponse(
						addResponseParsed as CartAddWithSections,
						lockToken
					)
					return
				}
			} catch (error) {
				cartDebug(
					'cart-drawer:runUpdateCycleBackground:add-exception',
					{
						error:
							error instanceof Error
								? error.message
								: String(error),
					}
				)
				console.error('Error adding GWP items', error)
			}
		}

		// If we get here without recursing (error paths), release the lock
		cartDebug('cart-drawer:runUpdateCycleBackground:release-lock', {
			lockToken,
		})
		this._clearUpdateState()
		releaseLock(lockToken)
	}

	/**
	 * Called when the last lock releases.
	 * Performs a single atomic section swap from the server response.
	 * The optimistic UI provided instant feedback; this replaces it
	 * with real server HTML so all controls (remove, quantity) work.
	 */
	async finalRender(): Promise<void> {
		const cart = getPendingResponse()
		if (!cart) return
		cartDebug('cart-drawer:finalRender:start', {
			hasSections: Boolean(cart.sections),
			itemCount: 'items' in cart ? cart.items.length : undefined,
			items: 'items' in cart ? summarizeCartItems(cart.items) : undefined,
		})

		// Reconcile client-side cart state with server truth.
		if ('item_count' in cart) {
			reconcile(cart as CartUpdateWithSections)
		} else if ('items' in cart) {
			void fetch('/cart.js', {
				headers: { 'Content-Type': 'application/json' },
			})
				.then((r) => r.json())
				.then((c) => {
					if (c?.items) reconcile(c)
				})
				.catch(() => {})
		}

		// Single atomic render — replaces optimistic items with real server HTML
		if (this.hasRenderableSections(cart.sections)) {
			renderSectionsOnce(cart.sections, this.getSectionsToRender())
			const itemsEl = q.os('cart-drawer-items', this)
			cartDebug('cart-drawer:finalRender:sections-rendered', {
				domItems: summarizeDomCartItems(itemsEl ?? null),
			})
		} else {
			// Sections missing from response - force refresh to get fresh sections
			cartDebug('cart-drawer:finalRender:missing-sections-refresh')
			await this.forceRefreshFromServer()
			return
		}

		enableCheckoutButton()
		this.updateCartEmptyState(cart)
		this.updateMembershipNotice(cart)
		void window.Ucoast.mediaManager.playAllInContainer(this)
	}

	private _clearUpdateState(): void {
		window.__skipGwpAdditions = false
		this._isRunningUpdateInstructions = false
		this._justAddedVariantIds = new Set()
	}

	/**
	 * Acquire a lock for an optimistic operation and register finalRender
	 * as the callback for when all locks release.
	 */
	acquireOptimisticLock(name: string): LockToken {
		const token = acquireLock(name)
		cartDebug('cart-drawer:acquireOptimisticLock', {
			name,
			token,
		})
		// Disable checkout while the cart is being validated
		disableCheckoutButton()
		// Always re-register the callback so it fires for the latest set of locks
		setOnAllReleasedCallback(() => void this.finalRender())
		return token
	}

	async renderContents(
		cart: CartAddWithSections | CartUpdateWithSections
	): Promise<void> {
		cartDebug('cart-drawer:renderContents:start', {
			isLocked: isLocked(),
			itemCount: 'items' in cart ? cart.items.length : undefined,
			instructions: summarizeInstructions(getUpdateInstructions(cart)),
		})
		// If an optimistic lock is held, defer rendering — store response for finalRender
		if (isLocked()) {
			setPendingResponse(cart)
			cartDebug('cart-drawer:renderContents:deferred-locked')
			return
		}

		const cartUpdateInstructions = getUpdateInstructions(cart)
		// When manually removing a GWP/bundle line, skip all additions for this pass
		if (window.__skipGwpAdditions) {
			cartUpdateInstructions.additions = []
			cartUpdateInstructions.update_required =
				Object.keys(cartUpdateInstructions.updates).length > 0
		}
		// When in an update cycle, only suppress additions for items we just added
		// (prevents infinite re-add loops) but allows NEW additions through
		else if (
			this._isRunningUpdateInstructions &&
			this._justAddedVariantIds.size > 0
		) {
			cartUpdateInstructions.additions =
				cartUpdateInstructions.additions.filter(
					(a) => !this._justAddedVariantIds.has(a.id)
				)
			cartUpdateInstructions.update_required =
				cartUpdateInstructions.additions.length > 0 ||
				Object.keys(cartUpdateInstructions.updates).length > 0
		}

		const hasUpdates = cartUpdateInstructions.update_required

		if (hasUpdates) {
			// Update cycle still needed (e.g. removing old exclusive items).
			// Don't render yet — run the cycle first, then render the final
			// response once to avoid a double-render flash.
			// MUST await so the caller knows the cycle finished — otherwise
			// fire-and-forget side effects compete for Shopify's session lock.
			await this.runUpdateInstructions(cartUpdateInstructions)
			cartDebug('cart-drawer:renderContents:ran-update-instructions')
			return
		}

		// All updates resolved — render sections and open drawer
		this._clearUpdateState()
		this.setActiveElement(document.activeElement)

		// If sections are missing (e.g., stripped to avoid stale data), force a
		// refresh from server to get fresh sections with current cart state.
		if (!this.hasRenderableSections(cart.sections)) {
			cartDebug('cart-drawer:renderContents:missing-sections-refresh')
			await this.forceRefreshFromServer()
			return
		}

		this.getSectionsToRender().forEach((section) => {
			const sectionHandle = section.section
			if (!sectionHandle || sectionHandle === 'cart-update-instructions')
				return
			const sectionHTML = cart.sections[sectionHandle]
			if (!sectionHTML) return
			if (sectionHandle === 'dynamic-progress-bar') {
				updateProgressBar(sectionHTML)
				return
			}
			renderRawHTMLToDOM({
				sourceHTML: sectionHTML,
				sourceSelector: section.selector,
				destinationSelector: section.selector,
				destinationSelectorContainer: `#${section.id}`,
			})
		})

		const itemsEl = q.os('cart-drawer-items', this)
		cartDebug('cart-drawer:renderContents:complete', {
			domItems: summarizeDomCartItems(itemsEl ?? null),
		})

		if (!this.classList.contains('active')) {
			await new Promise<void>((resolve) => {
				setTimeout(() => {
					this.getOverlay().addEventListener(
						'click',
						this.close.bind(this)
					)
					this.open()
					this.updateCartEmptyState(cart)
					void window.Ucoast.mediaManager.playAllInContainer(this)
					resolve()
				}, 1)
			})
		} else {
			this.updateCartEmptyState(cart)
		}

		this.updateMembershipNotice(cart)

		// Track Cart Viewed now that render is complete and the session
		// lock is free (skipped during open() when loading was shown)
		this.trackCartViewed()
	}

	private updateCartEmptyState(
		cart: CartAddWithSections | CartUpdateWithSections
	) {
		const cartDrawerItemsEl = q.os<HTMLElement>('cart-drawer-items', this)
		const renderedStateIsEmpty = cartDrawerItemsEl?.hasAttribute(
			ATTRIBUTES.cartEmpty
		)
		// Prefer the rendered section state because AJAX responses can vary.
		if (typeof renderedStateIsEmpty === 'boolean') {
			if (renderedStateIsEmpty) {
				this.setAttribute(ATTRIBUTES.cartEmpty, '')
			} else {
				this.removeAttribute(ATTRIBUTES.cartEmpty)
			}
		} else if (cart.items.length) {
			this.removeAttribute(ATTRIBUTES.cartEmpty)
		} else {
			this.setAttribute(ATTRIBUTES.cartEmpty, '')
		}
	}

	updateMembershipNotice(cart: CartAddWithSections | CartUpdateWithSections) {
		const notice = q.os('[data-membership-notice]')
		if (!notice) return

		const hasBundleOrMembership = cart.items.some((item) => {
			const productType = (
				((item as Record<string, unknown>).product_type as string) || ''
			).toLowerCase()
			return productType === 'bundle' || productType === 'streaming'
		})

		notice.classList.toggle('hidden', !hasBundleOrMembership)
	}

	/**
	 * Run the update cycle with minimal intermediate requests.
	 * Intermediate steps only request `cart-update-instructions` (fast ~200-400ms)
	 * instead of all 5 sections (~1.3s). Once the cycle is done, a single final
	 * request fetches all sections for rendering.
	 */
	/**
	 * Run the GWP/membership update cycle.
	 * Every request includes all sections so the last response can render
	 * directly — no extra "fetch final sections" round trip needed.
	 */
	async runUpdateInstructions(
		instructions: CartUpdateInstructions
	): Promise<void> {
		cartDebug('cart-drawer:runUpdateInstructions:start', {
			instructions: summarizeInstructions(instructions),
		})
		const sections = this.getSectionsToRender().map((s) => s.section)
		let cycleInstructions = instructions
		let lastResponse: CartAddWithSections | CartUpdateWithSections | null =
			null
		let cycleCount = 0
		const maxCycles = 5

		while (cycleCount < maxCycles) {
			cycleCount++
			cartDebug('cart-drawer:runUpdateInstructions:cycle', {
				cycleCount,
				instructions: summarizeInstructions(cycleInstructions),
			})

			if (cycleInstructions.additions.length > 0) {
				this._isRunningUpdateInstructions = true
				this._justAddedVariantIds = new Set(
					cycleInstructions.additions.map((a) => a.id)
				)

				try {
					cartDebug('cart-drawer:runUpdateInstructions:add-request', {
						additions: summarizeCartItems(
							cycleInstructions.additions
						),
					})
					const parsed = await this.requestCartMutationWithRetry(
						`${window.routes.cart_add_url}`,
						{
							items: cycleInstructions.additions,
							sections,
							sections_url: window.routes.cart_url,
						},
						'add'
					)
					if (!parsed) {
						throw new Error('Add request failed')
					}
					cartDebug(
						'cart-drawer:runUpdateInstructions:add-response',
						{
							hasError: 'status' in parsed,
							itemCount: parsed.item_count,
							items: summarizeCartItems(
								(parsed.items as CartAddWithSections['items']) ??
									[]
							),
						}
					)

					if ('status' in parsed) {
						console.error(
							'[CartDrawer] GWP addition failed:',
							parsed
						)
						break
					}

					lastResponse = parsed as CartAddWithSections
					const next = getUpdateInstructions(
						parsed as CartAddWithSections
					)
					if (this._justAddedVariantIds.size > 0) {
						next.additions = next.additions.filter(
							(a) => !this._justAddedVariantIds.has(a.id)
						)
						next.update_required =
							next.additions.length > 0 ||
							Object.keys(next.updates).length > 0
					}

					if (!next.update_required) break
					cycleInstructions = next
					continue
				} catch (error) {
					cartDebug(
						'cart-drawer:runUpdateInstructions:add-exception',
						{
							error:
								error instanceof Error
									? error.message
									: String(error),
						}
					)
					console.error('Error adding GWP items', error)
					break
				}
			}

			if (Object.keys(cycleInstructions.updates).length > 0) {
				try {
					cartDebug(
						'cart-drawer:runUpdateInstructions:update-request',
						{
							updates: cycleInstructions.updates,
						}
					)
					const parsed = await this.requestCartMutationWithRetry(
						`${window.routes.cart_update_url}`,
						{
							updates: cycleInstructions.updates,
							sections,
							sections_url: window.routes.cart_url,
						},
						'update'
					)
					if (!parsed) {
						throw new Error('Update request failed')
					}
					cartDebug(
						'cart-drawer:runUpdateInstructions:update-response',
						{
							itemCount: parsed.item_count,
							items: summarizeCartItems(
								(parsed.items as CartUpdateWithSections['items']) ??
									[]
							),
						}
					)
					if ('status' in parsed) {
						console.error(
							'[CartDrawer] Update instructions failed:',
							parsed
						)
						break
					}
					lastResponse = parsed as CartUpdateWithSections
					const next = getUpdateInstructions(
						parsed as CartUpdateWithSections
					)

					if (!next.update_required) break
					cycleInstructions = next
					continue
				} catch (error) {
					cartDebug(
						'cart-drawer:runUpdateInstructions:update-exception',
						{
							error:
								error instanceof Error
									? error.message
									: String(error),
						}
					)
					console.error('Error running update instructions', error)
					break
				}
			}

			break
		}

		// Render directly from the last cycle response — no extra fetch needed
		this._clearUpdateState()
		cartDebug('cart-drawer:runUpdateInstructions:done', {
			hasLastResponse: Boolean(lastResponse),
			hasSections: Boolean(lastResponse?.sections),
		})
		if (lastResponse?.sections) {
			await this.renderContents(lastResponse)
		}
	}

	/**
	 * Force-refresh the cart drawer from server state.
	 * Used as error recovery when optimistic updates fail.
	 */
	async forceRefreshFromServer(): Promise<void> {
		cartDebug('cart-drawer:forceRefreshFromServer:start')
		forceReleaseAllLocks()
		try {
			const sections = this.getSectionsToRender().map((s) => s.section)
			const response = await fetch(window.routes.cart_update_url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: JSON.stringify({
					updates: {},
					sections,
					sections_url: window.routes.cart_url,
				}),
			})
			const cart = await response.json()
			cartDebug('cart-drawer:forceRefreshFromServer:response', {
				itemCount: cart.item_count,
				items: summarizeCartItems(cart.items ?? []),
				hasSections: Boolean(cart.sections),
			})
			if (cart.sections) {
				await this.renderContents(cart)
			}
		} catch {
			// Last resort — reload
			console.error('[CartDrawer] Force refresh failed')
		}
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'CartDrawer',
				section: 'cart-drawer-items',
				selector: 'cart-drawer-items',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartDrawerIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartDrawer',
				section: 'dynamic-progress-bar',
				selector: 'dynamic-progress-bar',
			},
			{
				id: 'CartDrawer',
				section: 'dynamic-cart-footer',
				selector: '.drawer__footer',
			},
			{
				id: 'CartDrawer',
				section: 'cart-update-instructions',
				selector: '[data-cart-update-instructions]',
			},
		]
	}

	setActiveElement(element: Element | null) {
		if (!element) throw new Error('Active element is required')
		this.activeElement = element as HTMLElement
	}
}
